import { Checkbox, IconButton, MenuItem, Select, TextField, Tooltip } from '@mui/material'
import { ReactComponent as QuestionMarkIcon } from 'images/icons/faq.svg'
import clsx from 'clsx'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BC_DIRECTION, CLEANING_SUB_MODE, SUB_CLEAN_MODES } from 'utils/constants'
import { ICleaningModeConfig, IManualCleaningParams } from 'utils/interfaces'

import style from './ManualCleaningParams.module.css'

const inputSx = {
  '& .MuiInputBase-root': {
    height: '40px',
    fontSize: '14px',
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
  },
}

const selectSx = {
  width: '100%',
  '& .MuiSelect-select': {
    fontSize: '14px',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
}

const selectMenuProps = {
  sx: {
    '& .MuiMenuItem-root': {
      fontSize: '14px',
    },
  },
}

const ParamCheckbox = ({ checked, label, onChange }: { checked: boolean; label: string; onChange: () => void }) => (
  <div className={style.parametersString}>
    <span className={style.parametersLabel}>{label}</span>
    <div className={style.parametersInput}>
      <Checkbox checked={checked} onChange={onChange} />
    </div>
  </div>
)


const ParamTextFieldWithConstraints = ({
  value,
  label,
  min,
  max,
  step,
  tooltip,
  onChange,
}: {
  value: number
  label: string
  min: number
  max: number
  step: number
  tooltip?: string
  onChange: (value: number) => void
}) => {
  const [touched, setTouched] = useState(false)
  const hasError = touched && (value < min || value > max)

  const constraintText = max === Number.MAX_VALUE
    ? `> ${min}`
    : min === Number.MIN_VALUE
      ? `< ${max}`
      : `${min}–${max}`

  return (
    <div className={style.parametersString}>
      <span className={style.parametersLabel}>{label}</span>
      <div className={style.parametersInput}>
        <TextField
          sx={{
            ...inputSx,
            '& .MuiInputBase-input': {
              color: hasError ? '#dc664d' : undefined,
            },
            '& fieldset': {
              border: hasError ? '1px solid #dc664d' : undefined,
            },
          }}
          type="number"
          inputProps={{ min, max, step }}
          value={value.toString()}
          onChange={e => onChange(Number(e.target.value))}
          onBlur={() => setTouched(true)}
        />
        <span className={clsx(style.constraintLabel, hasError && style.constraintLabelError)}>{constraintText}</span>
        {tooltip && (
          <Tooltip title={tooltip} placement="top">
            <IconButton size="small" sx={{ padding: 0, marginLeft: '4px' }}>
              <QuestionMarkIcon className={style.tooltipIcon} />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

const ParamSelect = <T extends string | number>({
  value,
  label,
  options,
  onChange,
}: {
  value: T
  label: string
  options: { value: T; label: string }[]
  onChange: (value: T) => void
}) => (
  <div className={style.parametersString}>
    <span className={style.parametersLabel}>{label}</span>
    <div className={style.parametersInput}>
      <Select sx={selectSx} MenuProps={selectMenuProps} value={value} onChange={e => onChange(e.target.value as T)}>
        {options.map(opt => (
          <MenuItem key={String(opt.value)} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </div>
  </div>
)

const BcParamsBlock = ({
  bc,
  t,
  onChange,
}: {
  bc: { enabled: boolean; passageQuantity: number; speed: number; direction: BC_DIRECTION }
  t: (key: string) => string
  onChange: (patch: Partial<typeof bc>) => void
}) => (
  <div className={style.parametersType}>
    <span className={style.parametersSubTitle}>{t('zone_details.bc_params')}</span>
    <ParamCheckbox
      checked={bc.enabled}
      label={t('zone_details.enable')}
      onChange={() => onChange({ enabled: !bc.enabled })}
    />
    <ParamSelect
      value={bc.passageQuantity}
      label={t('zone_details.passage_quantity')}
      options={[
        { value: 0, label: t('zone_details.bc_loop_extended') },
        { value: 1, label: t('zone_details.bc_loop_folded') },
        { value: 2, label: t('zone_details.bc_loop_both') },
      ]}
      onChange={value => onChange({ passageQuantity: value })}
    />
    <ParamTextFieldWithConstraints
      value={bc.speed}
      label={t('zone_details.speed')}
      min={0}
      max={1.5}
      step={0.1}
      tooltip={t('zone_details.speed_tooltip')}
      onChange={value => onChange({ speed: value })}
    />
    <ParamSelect
      value={bc.direction}
      label={t('zone_details.direction')}
      options={[
        { value: BC_DIRECTION.CW, label: t('zone_details.cw') },
        { value: BC_DIRECTION.CCW, label: t('zone_details.ccw') },
      ]}
      onChange={value => onChange({ direction: value })}
    />
  </div>
)

const CornersParamsBlock = ({
  corners,
  t,
  onChange,
}: {
  corners: { enabled: boolean; speed: number }
  t: (key: string) => string
  onChange: (patch: Partial<typeof corners>) => void
}) => (
  <div className={style.parametersType}>
    <span className={style.parametersSubTitle}>{t('zone_details.corners_params')}</span>
    <ParamCheckbox
      checked={corners.enabled}
      label={t('zone_details.enable')}
      onChange={() => onChange({ enabled: !corners.enabled })}
    />
    <ParamTextFieldWithConstraints
      value={corners.speed}
      label={t('zone_details.speed')}
      min={0}
      max={1.5}
      step={0.1}
      tooltip={t('zone_details.speed_tooltip')}
      onChange={value => onChange({ speed: value })}
    />
  </div>
)

const ManualCleaningParams = ({
  config,
  onChange,
}: {
  config?: ICleaningModeConfig
  onChange: (config: ICleaningModeConfig) => void
}) => {
  const { t } = useTranslation()
  const initialParams = config && 'params' in config ? config.params : undefined

  const initialSelectedItem = (() => {
    if (!initialParams) return ''
    if (initialParams.crc) return CLEANING_SUB_MODE.FULL_COVERAGE
    if (initialParams.th) return CLEANING_SUB_MODE.TRASH_HUNTER
    if (initialParams.bc) return CLEANING_SUB_MODE.BORDER_CLEANING
    return ''
  })()

  const [selectedItem, setSelectedItem] = useState<CLEANING_SUB_MODE | ''>(initialSelectedItem)
  const [formData, setFormData] = useState({
    crc: initialParams?.crc ?? {
      enabled: false,
      overlap: 5,
      passageQuantity: 1,
      speed: 0.7,
    },
    bc: initialParams?.bc ?? {
      enabled: false,
      passageQuantity: 1,
      speed: 0.7,
      direction: BC_DIRECTION.CW,
    },
    corners: initialParams?.corners ?? {
      enabled: false,
      speed: 0.7,
    },
    th: initialParams?.th ?? {
      coverageWidth: 1.0,
      passageRepetitions: 1,
      speed: 0.7,
    },
  })

  const updateField = <K extends keyof typeof formData>(key: K, patch: Partial<(typeof formData)[K]>) => {
    const value = { ...formData[key], ...patch }
    setFormData(prev => ({ ...prev, [key]: value }))
    onChange({ mode: SUB_CLEAN_MODES.MANUAL, params: { ...initialParams, [key]: value } })
  }

  const changeSubMode = (subMode: CLEANING_SUB_MODE) => {
    if (subMode === selectedItem) return
    setSelectedItem(subMode)
    let params: IManualCleaningParams

    if (subMode === CLEANING_SUB_MODE.FULL_COVERAGE) {
      params = { crc: formData.crc, bc: formData.bc, corners: formData.corners }
    } else if (subMode === CLEANING_SUB_MODE.TRASH_HUNTER) {
      params = { th: formData.th }
    } else if (subMode === CLEANING_SUB_MODE.BORDER_CLEANING) {
      params = { bc: formData.bc, corners: formData.corners }
    } else {
      return
    }

    onChange({ mode: SUB_CLEAN_MODES.MANUAL, params })
  }

  return (
    <div className={style.root}>
      <div className={style.content}>
        <div className={clsx('text-14-400', style.subtitle)}>{t('zone_details.cleaning_type')}</div>
        <Select
          sx={selectSx}
          MenuProps={selectMenuProps}
          value={selectedItem}
          onChange={e => changeSubMode(e.target.value as CLEANING_SUB_MODE)}
        >
          <MenuItem value={CLEANING_SUB_MODE.FULL_COVERAGE}>{t('zone_details.submode_full_coverage')}</MenuItem>
          <MenuItem value={CLEANING_SUB_MODE.TRASH_HUNTER}>{t('zone_details.submode_trash_hunter')}</MenuItem>
          <MenuItem value={CLEANING_SUB_MODE.BORDER_CLEANING}>{t('zone_details.submode_border_cleaning')}</MenuItem>
        </Select>

        {selectedItem === CLEANING_SUB_MODE.FULL_COVERAGE && (
          <div className={style.parametersHolder}>
            <div className={style.parametersType}>
              <span className={style.parametersSubTitle}>{t('zone_details.crc_params')}</span>
              <ParamCheckbox
                checked={formData.crc.enabled}
                label={t('zone_details.enable')}
                onChange={() => updateField('crc', { enabled: !formData.crc.enabled })}
              />
              <ParamTextFieldWithConstraints
                value={formData.crc.overlap}
                label={t('zone_details.overlap')}
                min={5}
                max={50}
                step={1}
                tooltip={t('zone_details.overlap_tooltip')}
                onChange={value => updateField('crc', { overlap: value })}
              />
              <ParamTextFieldWithConstraints
                value={formData.crc.passageQuantity}
                label={t('zone_details.passage_quantity')}
                min={1}
                max={10}
                step={1}
                tooltip={t('zone_details.passage_quantity_tooltip')}
                onChange={value => updateField('crc', { passageQuantity: value })}
              />
              <ParamTextFieldWithConstraints
                value={formData.crc.speed}
                label={t('zone_details.speed')}
                min={0}
                max={1.5}
                step={0.1}
                tooltip={t('zone_details.speed_tooltip')}
                onChange={value => updateField('crc', { speed: value })}
              />
            </div>
            <BcParamsBlock bc={formData.bc} t={t} onChange={patch => updateField('bc', patch)} />
            <CornersParamsBlock corners={formData.corners} t={t} onChange={patch => updateField('corners', patch)} />
          </div>
        )}

        {selectedItem === CLEANING_SUB_MODE.TRASH_HUNTER && (
          <div className={style.parametersHolder}>
            <div className={style.parametersType}>
              <span className={style.parametersSubTitle}>{t('zone_details.th_params')}</span>
              <ParamTextFieldWithConstraints
                value={formData.th.coverageWidth}
                label={t('zone_details.coverage_width')}
                min={0}
                max={Number.MAX_VALUE}
                step={0.1}
                tooltip={t('zone_details.coverage_width_tooltip')}
                onChange={value => updateField('th', { coverageWidth: value })}
              />
              <ParamTextFieldWithConstraints
                value={formData.th.passageRepetitions}
                label={t('zone_details.passage_repetitions')}
                min={1}
                max={10}
                step={1}
                tooltip={t('zone_details.passage_repetitions_tooltip')}
                onChange={value => updateField('th', { passageRepetitions: value })}
              />
              <ParamTextFieldWithConstraints
                value={formData.th.speed}
                label={t('zone_details.speed')}
                min={0}
                max={1.5}
                step={0.1}
                tooltip={t('zone_details.speed_tooltip')}
                onChange={value => updateField('th', { speed: value })}
              />
            </div>
          </div>
        )}

        {selectedItem === CLEANING_SUB_MODE.BORDER_CLEANING && (
          <div className={style.parametersHolder}>
            <BcParamsBlock bc={formData.bc} t={t} onChange={patch => updateField('bc', patch)} />
            <CornersParamsBlock corners={formData.corners} t={t} onChange={patch => updateField('corners', patch)} />
          </div>
        )}
      </div>
    </div>
  )
}

export default ManualCleaningParams

